import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, MapPin, Layers, Truck, Crown, Rocket, Brain, Handshake, Target } from "lucide-react";
import Reveal, { useReveal } from "../components/Reveal";
import { useLang } from "../contexts/LangContext";
import { api } from "../lib/api";

const ICONS = [Sparkles, MapPin, Layers, Truck];

export default function Home() {
  const { t, lang } = useLang();
  const [settings, setSettings] = React.useState({});
  const [formats, setFormats] = React.useState([]);

  React.useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
    api.get("/formats").then((r) => setFormats(r.data || [])).catch(() => {});
  }, []);

  const heroImg = settings.hero_image;
  const aboutImg = settings.about_image;
  const stats = (settings.stats || []).slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative min-h-[100vh] flex items-end overflow-hidden"
        data-testid="hero-section"
      >
        {heroImg && (
          <img
            src={heroImg}
            alt="Scudieri Group hero"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
        )}
        <div className="absolute inset-0 hero-overlay" />
        <div className="grain" />

        <div className="relative container-x pb-20 lg:pb-32 pt-32">
          <Reveal>
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">
              {t.hero.eyebrow}
            </div>
          </Reveal>
          <Reveal delay={150}>
            <img
              src="/logo.png"
              alt="Scudieri Group"
              className="block w-[240px] sm:w-[280px] md:w-[340px] lg:w-[400px] h-auto drop-shadow-[0_20px_60px_rgba(0,0,0,0.7)]"
              data-testid="hero-logo"
            />
          </Reveal>
          <Reveal delay={250}>
            <h1 className="sr-only" data-testid="hero-title">
              Scudieri Group
            </h1>
          </Reveal>

          <Reveal delay={350}>
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <div className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight text-white">
                <span className="word-rotator align-baseline" aria-hidden>
                  {(t.hero.rotatorWords || ["Sogna.", "Crea.", "Realizza.", "Vivi."]).map((w, i) => (
                    <span key={i}>{w}</span>
                  ))}
                </span>
              </div>
              <span className="sticker" data-testid="hero-sticker">
                ✦ {t.hero.sticker || "Tutto è possibile"}
              </span>
            </div>
          </Reveal>

          <Reveal delay={450}>
            <p className="body-lg max-w-2xl mt-7 text-white/90" data-testid="hero-subtitle">
              {t.hero.subtitle}
            </p>
          </Reveal>
          <Reveal delay={550}>
            <p className="font-script text-2xl md:text-3xl text-[color:var(--gold)] mt-4">
              — {t.hero.tag}
            </p>
          </Reveal>
          <Reveal delay={700}>
            <div className="mt-10 flex flex-wrap gap-4" data-testid="hero-cta">
              <Link to="/contatti" className="btn-primary" data-testid="hero-cta-primary">
                {t.common.requestProposal}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/format" className="btn-secondary" data-testid="hero-cta-secondary">
                {t.common.discoverFormats}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/10 bg-[#0a0a0a] py-6" data-testid="hero-marquee">
        <div className="marquee">
          <div className="marquee-track">
            {Array.from({ length: 2 }).map((_, k) => (
              <React.Fragment key={k}>
                {[
                  "Food Truck",
                  "Street Food",
                  "Fiere",
                  "Manifestazioni",
                  "Event Restaurant",
                  "Aree Food",
                  "Milano",
                  "Dal 2016",
                  "Food Company",
                ].map((w, i) => (
                  <span
                    key={`${k}-${i}`}
                    className="font-display text-base text-white/55"
                  >
                    {w}
                    <span className="mx-12 text-[color:var(--gold)]">★</span>
                  </span>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT / NUMBERS */}
      <section className="section-pad bg-[#0a0a0a]" data-testid="impact-section">
        <div className="container-x">
          <Reveal>
            <div className="eyebrow mb-4">{t.impact.eyebrow}</div>
            <h2 className="h2 text-white max-w-3xl">{t.impact.title}</h2>
            <p className="body-lg mt-5 max-w-2xl">{t.impact.subtitle}</p>
          </Reveal>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="premium-card p-8 h-full flex flex-col gap-6" data-testid={`stat-${i}`}>
                    <Icon className="w-6 h-6 text-[color:var(--gold)]" />
                    <div className="ticker">{lang === "it" ? s.value_it : s.value_en}</div>
                    <div className="text-sm text-[color:var(--txt-secondary)] uppercase tracking-[0.16em] font-display">
                      {lang === "it" ? s.label_it : s.label_en}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section-pad bg-[#0a0a0a] border-t border-white/5" data-testid="about-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <div className="eyebrow mb-4">{t.about.eyebrow}</div>
            <h2 className="h2 text-white">{t.about.title}</h2>
            <p className="body-lg mt-6">{t.about.body}</p>
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.about.pillars.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-white/85">
                  <span className="mt-2 w-1.5 h-1.5 bg-[color:var(--gold)] rounded-full" />
                  <span className="font-display text-sm tracking-wide">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={150}>
            <div className="relative aspect-[4/5] zoom-img">
              {aboutImg && (
                <img src={aboutImg} alt="Scudieri Group operations" className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <div className="font-display text-sm tracking-[0.32em] text-[color:var(--gold)] uppercase">
                  Milano · 20125
                </div>
                <div className="text-white/80 text-sm mt-1">
                  Viale Lunigiana 8 — Sede operativa
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES GRID */}
      <ServicesPreview />

      {/* MANIFESTO + PILLARS */}
      <ManifestoBlock />
      <PillarsBlock />

      {/* FORMATS TEASER */}
      <FormatsTeaser formats={formats} />

      {/* ORGANIZERS */}
      <OrganizersBlock />

      {/* WHY US */}
      <WhyUsBlock />

      {/* CTA */}
      <CtaBlock />
    </div>
  );
}

function ServicesPreview() {
  const { t } = useLang();
  return (
    <section className="section-pad bg-[#080808] border-t border-white/5" data-testid="services-section">
      <div className="container-x">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow mb-4">{t.services.eyebrow}</div>
              <h2 className="h2 text-white max-w-3xl">{t.services.title}</h2>
            </div>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {t.services.items.map((s, i) => (
            <Reveal key={i} delay={(i % 3) * 80}>
              <div className="premium-card p-7 h-full" data-testid={`service-card-${i}`}>
                <div className="font-display text-xs tracking-[0.32em] uppercase text-[color:var(--gold)] mb-4">
                  0{i + 1}
                </div>
                <div className="h3 text-white mb-3">{s.t}</div>
                <p className="text-[color:var(--txt-secondary)] leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FormatsTeaser({ formats }) {
  const { t, lang } = useLang();
  return (
    <section className="section-pad bg-[#0a0a0a]" data-testid="formats-teaser">
      <div className="container-x">
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12 items-end">
            <div className="md:col-span-7">
              <div className="eyebrow mb-4">{t.formats.eyebrow}</div>
              <h2 className="h2 text-white">{t.formats.title}</h2>
            </div>
            <div className="md:col-span-5">
              <p className="body-lg">{t.formats.subtitle}</p>
            </div>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {formats.slice(0, 6).map((f, i) => {
            const span =
              i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-4" : i === 3 ? "md:col-span-4" : i === 4 ? "md:col-span-4" : "md:col-span-12";
            const ratio = i === 0 ? "aspect-[16/10]" : "aspect-[4/3]";
            return (
              <Reveal key={f.id} className={span} delay={i * 80}>
                <Link
                  to={`/format#${f.slug}`}
                  className="block premium-card group"
                  data-testid={`format-card-${f.slug}`}
                >
                  <div className={`relative ${ratio} zoom-img`}>
                    <img src={f.image_url} alt={f.title_it} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      {(f.tags || []).slice(0, 3).map((tag) => (
                        <span key={tag} className="badge-pill">{tag}</span>
                      ))}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="text-[10px] font-display tracking-[0.32em] uppercase text-[color:var(--gold)]">
                        {String(i + 1).padStart(2, "0")} / Format
                      </div>
                      <div className="h3 text-white mt-1">{lang === "it" ? f.title_it : f.title_en}</div>
                      <div className="text-sm text-white/70 mt-1">{lang === "it" ? f.tagline_it : f.tagline_en}</div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-12 flex">
          <Link to="/format" className="btn-secondary" data-testid="formats-see-all">
            {t.common.discoverFormats} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function OrganizersBlock() {
  const { t } = useLang();
  return (
    <section className="section-pad bg-[#080808] border-t border-white/5" data-testid="organizers-section">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <Reveal className="lg:col-span-5">
          <div className="eyebrow mb-4">{t.organizers.eyebrow}</div>
          <h2 className="h2 text-white">{t.organizers.title}</h2>
          <p className="body-lg mt-6">{t.organizers.body}</p>
          <Link to="/contatti" className="btn-primary mt-10" data-testid="organizers-cta">
            {t.common.contactUs} <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={150}>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {t.organizers.bullets.map((b, i) => (
              <li
                key={i}
                className="bg-[#0a0a0a] p-7 flex items-start gap-4"
                data-testid={`organizer-bullet-${i}`}
              >
                <span className="font-display text-xs tracking-[0.32em] text-[color:var(--gold)] mt-1">
                  0{i + 1}
                </span>
                <span className="text-white/90 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

function WhyUsBlock() {
  const { t } = useLang();
  return (
    <section className="section-pad bg-[#0a0a0a]" data-testid="why-section">
      <div className="container-x">
        <Reveal>
          <div className="eyebrow mb-4">{t.why.eyebrow}</div>
          <h2 className="h2 text-white max-w-3xl">{t.why.title}</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {t.why.points.map((p, i) => (
            <Reveal key={i} delay={(i % 4) * 80}>
              <div className="border border-white/10 p-7 h-full hover:border-[color:var(--gold)]/60 transition-colors" data-testid={`why-card-${i}`}>
                <div className="font-display text-xs tracking-[0.32em] uppercase text-[color:var(--gold)] mb-3">
                  · {String(i + 1).padStart(2, "0")}
                </div>
                <div className="h3 text-white mb-2">{p.t}</div>
                <p className="text-[color:var(--txt-secondary)] text-sm leading-relaxed">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBlock() {
  const { t } = useLang();
  const { ref, visible } = useReveal();
  return (
    <section className="relative overflow-hidden bg-[#0a0a0a] border-t border-white/5" data-testid="cta-section">
      <div className="absolute inset-0 opacity-50">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 80% 30%, rgba(255,69,0,0.18), transparent 55%), radial-gradient(circle at 20% 70%, rgba(212,175,55,0.18), transparent 55%)",
          }}
        />
      </div>
      <div ref={ref} className={`relative container-x section-pad ${visible ? "" : ""}`}>
        <div className="max-w-4xl">
          <div className="eyebrow mb-6">{t.cta.eyebrow}</div>
          <h2 className="h1 text-white" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
            {t.cta.title}
          </h2>
          <p className="body-lg mt-8 max-w-2xl">{t.cta.body}</p>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/contatti" className="btn-primary" data-testid="cta-primary">
              {t.common.contactUs} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/format" className="btn-ghost" data-testid="cta-secondary">
              {t.common.discoverFormats}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------- MANIFESTO ----------------
function ManifestoBlock() {
  const { t } = useLang();
  const m = t.manifesto || {};
  return (
    <section className="paint-bg section-pad bg-[#0a0a0a] border-t border-white/5" data-testid="manifesto-section">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-5">
          <div className="eyebrow mb-4">{m.eyebrow}</div>
          <span className="brush-line mb-6" />
          <p className="font-script text-3xl md:text-4xl text-[color:var(--gold)] leading-tight">
            {m.lead}
          </p>
          <p className="body-lg mt-6 max-w-md">{m.body}</p>
          <p className="font-display font-bold text-xl md:text-2xl text-white mt-8 italic">
            “{m.claim}”
          </p>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={150}>
          <div className="relative flex items-center justify-center">
            <div className="absolute w-[80%] h-[80%] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.18),transparent_70%)]" />
            <img
              src="/logo.png"
              alt="Scudieri Group badge"
              className="relative w-full max-w-md mx-auto drop-shadow-[0_24px_60px_rgba(0,0,0,0.7)]"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ---------------- PILLARS ----------------
const PILLAR_ICONS = {
  Passione: Crown,
  Visione: Rocket,
  Mente: Brain,
  Relazioni: Handshake,
  Obiettivi: Target,
  Passion: Crown,
  Vision: Rocket,
  Mind: Brain,
  Relationships: Handshake,
  Goals: Target,
};

const PILLAR_COLOR = {
  gold: "var(--gold)",
  green: "var(--it-green)",
  white: "#ffffff",
  red: "var(--it-red)",
};

function PillarsBlock() {
  const { t } = useLang();
  const p = t.pillars || {};
  const items = p.items || [];
  return (
    <section className="section-pad bg-[#080808] border-t border-white/5" data-testid="pillars-section">
      <div className="container-x">
        <Reveal>
          <div className="eyebrow mb-4">{p.eyebrow}</div>
          <h2 className="h2 text-white max-w-3xl">{p.title}</h2>
          {p.subtitle && <p className="body-lg mt-5 max-w-2xl">{p.subtitle}</p>}
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {items.map((it, i) => {
            const Icon = PILLAR_ICONS[it.t] || Sparkles;
            const accent = PILLAR_COLOR[it.color] || "var(--gold)";
            return (
              <Reveal key={i} delay={i * 90}>
                <div
                  className="pillar-card h-full"
                  style={{ "--accent": accent }}
                  data-testid={`pillar-${i}`}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center mb-5"
                    style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${accent}` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: accent }} />
                  </div>
                  <div className="font-display font-bold text-2xl text-white">{it.t}</div>
                  <div className="font-script text-2xl mt-1" style={{ color: accent }}>
                    {it.d}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={200}>
          <div className="mt-12 text-center">
            <span className="sticker" style={{ transform: "rotate(2deg)" }}>
              ✦ Sogna · Crea · Realizza · Vivi
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
